/**
 * Every human judgement made on a case.
 *
 * **Append-only, and an input to every rebuild.** ADR-0008 makes the human gate mandatory —
 * nothing merges without a person — which means the record of who decided what is part of
 * explaining an entity, not an afterthought. `GET /cases/{id}/review-decisions` has shipped
 * since M4 and nothing displayed it, so "why are these two the same person?" could only be
 * answered by whoever happened to remember.
 *
 * `prior_state` is shown because a decision read a year later is otherwise unintelligible:
 * the projection has been rebuilt since, the scores have moved, and without the state as
 * it stood the reviewer's reasoning cannot be reconstructed.
 */

import { useState, type ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { request } from '@/lib/api';
import type { ReverseMergeRequest, ReviewDecisionLog } from '@/lib/schemas';
import { Badge, Button, ErrorState, Loading, Table } from '@/components/ui';
import { useCan } from '@/hooks/usePermissions';

/** The decision log for one case. */
export function DecisionLog({ caseId }: { caseId: string }): ReactElement {
  const [expanded, setExpanded] = useState<string | null>(null);
  const [reversing, setReversing] = useState<string | null>(null);
  const [rationale, setRationale] = useState('');
  const canApprove = useCan('approve_merges');
  const queryClient = useQueryClient();

  // **Undoing a merge is a decision about a decision.** It records a new row rather than
  // editing the approval, adds a content-keyed `never_merge`, and rebuilds the projection
  // — the rebuild is what an analyst actually sees, because the links the merge wrote stay
  // active until the projection is regenerated with the approval skipped.
  const reverse = useMutation({
    mutationFn: (proposalId: string) =>
      request<unknown, ReverseMergeRequest>(`/merge-proposals/${proposalId}:reverse`, {
        method: 'POST',
        body: { rationale },
      }),
    onSuccess: () => {
      setReversing(null);
      setRationale('');
      for (const key of ['review-decisions', 'entities', 'proposals', 'assertions']) {
        void queryClient.invalidateQueries({ queryKey: [key, caseId] });
      }
    },
  });

  const decisions = useQuery({
    queryKey: ['review-decisions', caseId],
    queryFn: () => request<ReviewDecisionLog[]>(`/cases/${caseId}/review-decisions`),
  });

  if (decisions.isLoading) return <Loading />;
  if (decisions.error) return <ErrorState error={decisions.error} />;

  const rows = decisions.data ?? [];

  return (
    <div className="space-y-3">
      <h2 className="text-sm font-semibold">Decision log</h2>
      <p className="max-w-3xl text-xs text-muted">
        Every judgement a person made on this case, oldest first. Append-only: a decision is
        never edited or removed, because it is an input to every rebuild of the entity
        projection.
      </p>

      <Table
        headers={['Decided', 'About', 'Decision', 'By', 'Rationale', '']}
        empty="Nobody has decided anything on this case yet."
        rows={rows.map((entry) => [
          <span key="t" className="text-xs text-muted">
            {entry.decided_at.slice(0, 19).replace('T', ' ')}
          </span>,
          <span key="s" className="text-xs">
            {entry.subject_type}{' '}
            <span className="font-mono text-muted">{entry.subject_id.slice(0, 8)}</span>
          </span>,
          // The testid is on the wrapper: `Badge` accepts only `children` and `tone`, so
          // a `data-*` prop passed to it is silently dropped.
          <span key="d" data-testid="decision-value">
            <Badge
              // The stored value is the request verb — `approve`, `reject`, `defer` — not
              // a past participle. Matching on "approved" left every badge muted.
              tone={
                entry.decision === 'approve'
                  ? 'ok'
                  : entry.decision === 'reject'
                    ? 'danger'
                    : 'muted'
              }
            >
              {entry.decision}
            </Badge>
          </span>,
          <span key="b" className="font-mono text-xs text-muted">
            {entry.decided_by.slice(0, 8)}
          </span>,
          <span key="r" className="text-xs">
            {entry.rationale || <span className="text-muted">— none given —</span>}
          </span>,
          <span key="x" className="flex items-center gap-2">
            <Button
              data-testid={`decision-state-${entry.id}`}
              onClick={() => setExpanded(expanded === entry.id ? null : entry.id)}
            >
              {expanded === entry.id ? 'Hide' : 'State at the time'}
            </Button>
            {/* Only an approved merge can be undone. A rejection needs no reversal: its
                `never_merge` already keeps the pair apart. */}
            {canApprove &&
              entry.decision === 'approve' &&
              entry.subject_type === 'merge_proposal' && (
                <Button
                  data-testid={`reverse-${entry.subject_id}`}
                  onClick={() =>
                    setReversing(reversing === entry.subject_id ? null : entry.subject_id)
                  }
                >
                  Undo merge
                </Button>
              )}
          </span>,
        ])}
      />

      {reversing && (
        <div className="space-y-2 rounded border border-line bg-base p-3">
          <p className="text-xs text-muted">
            This separates the two entities again. The approval stays in the log — the
            record must still show it happened and who made it — and a note is added
            beside it. The pair will not be proposed again.
          </p>
          <div className="flex flex-wrap items-end gap-2">
            <label className="block flex-1 text-sm">
              <span className="text-muted">Why are they not the same?</span>
              <input
                data-testid="reverse-rationale"
                value={rationale}
                onChange={(event) => setRationale(event.target.value)}
                className="mt-1 w-full rounded border border-line bg-surface px-2 py-1.5 text-sm"
              />
            </label>
            <Button
              data-testid="confirm-reverse"
              tone="primary"
              disabled={rationale.trim().length === 0 || reverse.isPending}
              onClick={() => reverse.mutate(reversing)}
            >
              {reverse.isPending ? 'Separating…' : 'Undo the merge'}
            </Button>
            <Button onClick={() => setReversing(null)}>Cancel</Button>
          </div>
          {reverse.error != null && <ErrorState error={reverse.error} />}
        </div>
      )}

      {expanded && (
        <pre
          data-testid="decision-prior-state"
          className="overflow-x-auto rounded border border-line bg-base p-3 font-mono text-xs"
        >
          {JSON.stringify(rows.find((entry) => entry.id === expanded)?.prior_state ?? {}, null, 2)}
        </pre>
      )}
    </div>
  );
}
